import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { Calendar, CalendarProps, momentLocalizer, View, Views } from 'react-big-calendar'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Toolbar from './Toolbar'
import moment from 'moment'
import { isRtlLanguage, normalizeLanguage, SupportedLanguage } from 'i18n'
import { formatLocalizedDate } from 'i18n/formatDate'
import { ICalendarEvent } from '../../../types'
import { CalendarColorsMap } from '../../../config/data'
const DnDCalendar = withDragAndDrop(Calendar as React.ComponentType<CalendarProps<any, object>>)
const localizer = momentLocalizer(moment)

// Map our SupportedLanguage codes to react-big-calendar's `culture` strings
// (which align with moment locale identifiers).
const CALENDAR_CULTURE: Record<SupportedLanguage, string> = {
  ar: 'ar',
  en: 'en',
  es: 'es',
  fr: 'fr',
  pl: 'pl',
  tl: 'fil',
  vi: 'vi',
  zh: 'zh-cn',
}

const getEventStyle = (
  event: ICalendarEvent
): {
  style: any
} => {
  let style: any = {
    minWidth: '50px',
    minHeight: '25px !important',
    borderRadius: '5px',
    padding: '5px',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '17px',
    role: 'calendar-event',
  }
  const hasEventPassed = moment(event.startDateTime).isBefore(moment())
  style = {
    ...style,
    backgroundColor: CalendarColorsMap.get(event.eventType),
    color: '#0085ff',
    opacity: hasEventPassed ? 0.5 : 1,
  }

  if (event.meeting === 'review') {
    style = { ...style, backgroundColor: '#7B61FF2B', color: '#7B61FF' }
  }
  if (event.meeting === 'playbook') {
    style = { ...style, backgroundColor: '#F4F4F5', color: '#52525B' }
  }
  if ((event as any).status === 'cancelled') {
    style = {
      ...style,
      backgroundColor: '#F3F4F6',
      color: '#9CA3AF',
      textDecoration: 'line-through',
      opacity: 0.7,
    }
  }
  return {
    style: style,
  }
}

export interface CalendarViewerProps {
  onSelectEvent: (calEvent: ICalendarEvent) => void
  isMobile: boolean
  view: View
  setView: (value: React.SetStateAction<View>) => void
  setDate: (date: Date) => void
  date: Date
  calendarEvents: ICalendarEvent[]
}
export const CalendarViewer = ({
  onSelectEvent,
  isMobile,
  setView,
  setDate,
  view,
  date,
  calendarEvents,
}: CalendarViewerProps) => {
  const { t, i18n } = useTranslation()
  const culture = CALENDAR_CULTURE[normalizeLanguage(i18n.language)]
  // react-big-calendar does not read <html dir>; without this prop it keeps
  // emitting its LTR column borders, time-header gutter and event padding while
  // the page around it is mirrored. `key={culture}` already remounts on a
  // language change, so the class swap takes effect cleanly.
  const isRtl = isRtlLanguage(i18n.language)
  // Keep moment's global locale in sync with the active language so the
  // localizer (created at module load) emits localized weekday/month names.
  useEffect(() => {
    moment.locale(culture)
  }, [culture])
  // RBC's default range/day header formats hardcode month-before-day ("MMMM DD"),
  // which is wrong for locales like fr. Use moment's locale-aware tokens instead.
  const formats = useMemo(
    () => ({
      dayRangeHeaderFormat: ({ start, end }, c, local) =>
        `${local.format(start, 'll', c)} – ${local.format(end, 'll', c)}`,
      dayHeaderFormat: (date, c, local) => local.format(date, 'dddd LL', c),
      dayFormat: (date, c, local) => local.format(date, 'ddd D', c),
    }),
    []
  )

  const messages = useMemo(
    () => ({
      today: t('calendar.today'),
      previous: t('calendar.back'),
      next: t('calendar.next'),
      month: t('calendar.month'),
      week: t('calendar.week'),
      day: t('calendar.day'),
      agenda: t('calendar.agenda'),
      date: t('calendar.date'),
      time: t('calendar.time'),
      event: t('calendar.event'),
      noEventsInRange: t('calendar.noEventsInRange'),
      showMore: (count: number) => t('calendar.showMore', { count }),
    }),
    [t]
  )
  // CAL-4: without this both week and day views open scrolled to 00:00.
  const scrollToTime = moment().startOf('day').add(8, 'hours').toDate()

  return (
    <div data-testid="calendar-viewer">
      <DnDCalendar
        key={culture}
        culture={culture}
        rtl={isRtl}
        drilldownView="month"
        popup={true}
        localizer={localizer}
        messages={messages}
        formats={formats}
        scrollToTime={scrollToTime}
        events={calendarEvents}
        endAccessor="end"
        startAccessor="start"
        view={view}
        onView={setView}
        date={date}
        onNavigate={setDate}
        style={{ height: 'calc(100vh - 67px)', position: 'relative' }}
        step={60}
        eventPropGetter={getEventStyle}
        onSelectEvent={onSelectEvent}
        onDrillDown={(e) => {
          setDate(e)
          setView(Views.DAY)
        }}
        showMultiDayTimes
        components={{
          toolbar: (params) => <Toolbar setDate={setDate} setView={setView} {...params} />,
          event: EventTitle,
        }}
      />
    </div>
  )
}

const EventTitle = ({ event }) => {
  const { i18n } = useTranslation()
  const isCancelled = event.status === 'cancelled'
  return (
    <span
      className={`text-xs ${isCancelled ? 'line-through' : ''}`}
      title={`${formatLocalizedDate(event.startDateTime, i18n.language, 'LT')} ${
        event.title ?? ''
      }`.trim()}
    >
      {formatLocalizedDate(event.startDateTime, i18n.language, 'LT')}
      <span className="font-semibold"> {event.title} </span>
    </span>
  )
}
